import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { motion, AnimatePresence } from 'framer-motion'
import { ChevronLeft, ChevronRight, Quote } from 'lucide-react'
import { useLanguage } from '../../context/LanguageContext'
import useStoryCards from '../../hooks/useStoryCards'
import { STUDENT_EXPERIENCES } from '../../data/siteContent'
import SectionHeading from '../ui/SectionHeading'

const PROGRAM_STYLES = {
  british: {
    badge: 'bg-primary text-white',
    ring: 'ring-primary/20',
    bar: 'bg-primary',
  },
  american: {
    badge: 'bg-accent text-white',
    ring: 'ring-accent/25',
    bar: 'bg-accent',
  },
  national: {
    badge: 'bg-secondary text-white',
    ring: 'ring-secondary/20',
    bar: 'bg-secondary',
  },
}

function StudentSlide({ item, t }) {
  const style = PROGRAM_STYLES[item.program] || PROGRAM_STYLES.british
  const programLabel = item.program ? t(`programs.${item.program}.title`) : ''

  return (
    <div className="grid overflow-hidden lg:grid-cols-[minmax(260px,340px)_1fr]">
      <div className="relative">
        <div className={`absolute inset-y-0 start-0 w-1.5 ${style.bar}`} aria-hidden />
        <div className="relative aspect-[4/5] sm:aspect-[3/4] lg:aspect-auto lg:min-h-[420px]">
          <img src={item.image} alt={item.name} className="h-full w-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-primary/70 via-transparent to-transparent lg:bg-gradient-to-r lg:from-transparent lg:via-transparent lg:to-white/10" />
          {programLabel ? (
            <span
              className={`absolute bottom-4 start-4 rounded-full px-3 py-1 text-xs font-bold uppercase tracking-wider shadow-lg ${style.badge}`}
            >
              {programLabel}
            </span>
          ) : null}
        </div>
      </div>

      <div className="relative flex flex-col justify-center px-6 py-8 sm:px-10 sm:py-10 lg:px-12 lg:py-12">
        <Quote className="mb-4 h-10 w-10 text-accent/80" aria-hidden />
        <blockquote className="text-xl font-medium leading-relaxed text-primary sm:text-2xl lg:text-[1.65rem] lg:leading-snug">
          <span className="text-accent">&ldquo;</span>
          {item.quote}
          <span className="text-accent">&rdquo;</span>
        </blockquote>

        <div className="mt-8 flex items-center gap-4 border-t border-border/60 pt-6">
          <div className={`h-14 w-14 shrink-0 overflow-hidden rounded-full ring-4 ${style.ring}`}>
            <img src={item.image} alt="" className="h-full w-full object-cover" />
          </div>
          <div>
            <p className="text-lg font-bold text-primary">{item.name}</p>
            <p className="text-sm font-medium text-text/55">{item.meta}</p>
          </div>
        </div>
      </div>
    </div>
  )
}

function buildFallbackSlides(t) {
  return STUDENT_EXPERIENCES.map((item) => ({
    id: item.key,
    image: item.image,
    program: item.program,
    name: t(`pagesExtras.students.${item.key}.name`),
    meta: t(`pagesExtras.students.${item.key}.meta`),
    quote: t(`pagesExtras.students.${item.key}.quote`),
  }))
}

export default function StudentVoicesSection() {
  const { t } = useTranslation()
  const { isRTL, localized } = useLanguage()
  const { items: apiItems } = useStoryCards('students')
  const [index, setIndex] = useState(0)

  const slides = useMemo(() => {
    if (apiItems.length) {
      return apiItems.map((item) => ({
        id: item.id,
        image: item.image,
        program: item.program,
        name: localized(item, 'name'),
        meta: localized(item, 'meta'),
        quote: localized(item, 'quote'),
      }))
    }
    return buildFallbackSlides(t)
  }, [apiItems, localized, t])

  const total = slides.length
  const current = slides[index] || slides[0]

  const prev = () => setIndex((i) => (i - 1 + total) % total)
  const next = () => setIndex((i) => (i + 1) % total)

  if (!current) return null

  return (
    <section className="relative my-10 overflow-hidden bg-white py-10 shadow-[inset_0_1px_0_0_rgba(93,16,56,0.06)] sm:my-14 sm:py-14 md:py-16">
      <div className="pointer-events-none absolute inset-0 overflow-hidden" aria-hidden>
        <div className="absolute -end-24 top-0 h-64 w-64 rounded-full bg-accent/5 blur-3xl" />
        <div className="absolute -start-16 bottom-0 h-48 w-48 rounded-full bg-primary/5 blur-3xl" />
      </div>

      <div className="relative mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
        <SectionHeading
          label={t('pagesExtras.studentsSayLabel')}
          title={t('pagesExtras.studentsSayTitle')}
          subtitle={t('pagesExtras.studentsSaySubtitle')}
        />

        <div className="overflow-hidden rounded-3xl border border-border/60 bg-background shadow-xl shadow-primary/5">
          <AnimatePresence mode="wait">
            <motion.div
              key={current.id}
              initial={{ opacity: 0, x: isRTL ? -24 : 24 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: isRTL ? 24 : -24 }}
              transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            >
              <StudentSlide item={current} t={t} />
            </motion.div>
          </AnimatePresence>
        </div>

        <div className="mt-8 flex flex-col gap-6 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex flex-wrap justify-center gap-2 sm:justify-start">
            {slides.map((item, i) => {
              const style = PROGRAM_STYLES[item.program] || PROGRAM_STYLES.british
              return (
                <button
                  key={item.id}
                  type="button"
                  onClick={() => setIndex(i)}
                  className={`flex items-center gap-2 rounded-full border px-3 py-1.5 text-left text-xs transition-all ${
                    i === index
                      ? 'border-primary bg-primary/5 shadow-sm'
                      : 'border-border/60 bg-white hover:border-primary/30'
                  }`}
                  aria-label={item.name}
                  aria-current={i === index ? 'true' : undefined}
                >
                  <span
                    className={`h-7 w-7 shrink-0 overflow-hidden rounded-full ring-2 ${
                      i === index ? style.ring : 'ring-border/40'
                    }`}
                  >
                    <img src={item.image} alt="" className="h-full w-full object-cover" />
                  </span>
                  <span className={`hidden font-semibold sm:inline ${i === index ? 'text-primary' : 'text-text/60'}`}>
                    {item.name}
                  </span>
                </button>
              )
            })}
          </div>

          <div className="flex items-center justify-center gap-3">
            <button
              type="button"
              onClick={prev}
              className="rounded-full border border-border/60 bg-white p-2.5 text-primary transition-colors hover:border-primary/30 hover:bg-primary/5"
              aria-label={t('pagesExtras.studentsSayPrev')}
            >
              <ChevronLeft className={`h-5 w-5 ${isRTL ? 'rotate-180' : ''}`} />
            </button>
            <span className="min-w-[3rem] text-center text-sm font-semibold text-text/50">
              {index + 1} / {total}
            </span>
            <button
              type="button"
              onClick={next}
              className="rounded-full border border-border/60 bg-white p-2.5 text-primary transition-colors hover:border-primary/30 hover:bg-primary/5"
              aria-label={t('pagesExtras.studentsSayNext')}
            >
              <ChevronRight className={`h-5 w-5 ${isRTL ? 'rotate-180' : ''}`} />
            </button>
          </div>
        </div>
      </div>
    </section>
  )
}
